import { useState, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui-system";
import {
  apiErrorMessage, useTemplateRecipients, RecipientsCard, EmailPreviewHeader,
} from "@/components/EmailTemplateRecipients";
import { toast } from "sonner";
import { fetchWouldOverwrite, lookupMessage, prefillFromLookup } from "@/lib/incorrectWeightLookup";
import { Plus, Trash2, Send, Loader2, Package, Scale, Search } from "lucide-react";

/**
 * Incorrect Weight Charges Email Notification — a manual letter from the EdgeOS Support Team 
 * to the carrier listing LRNs whose dimensions were recorded wrongly.
 *
 * "Fetch details" prefills a shipment's LRN, Total Boxes Sent and the Delhivery-record
 * dimensions from the order (GET …/incorrect-weight-charges/lookup, by LRN and/or Order
 * ID). It is only a starting point: every prefilled value stays editable and the
 * Client Confirmation side is always typed. Nothing the lookup returns is sent — the
 * email carries what is on the form.
 *
 * Two separate actions, deliberately:
 *  - Save Recipients persists To/CC/BCC in the notification_configs doc for
 *    ACTION_TYPE via the normal notification-config endpoints.
 *  - Trigger Now posts ONLY the shipment data; the backend sends to the SAVED
 *    recipients (backend/services/incorrect_weight_charges_service.py), so it is
 *    blocked while recipient edits are unsaved — the preview never shows a list
 *    the email will not actually go to.
 *
 * The preview wording mirrors render() in that service — change both together.
 */
export const INCORRECT_WEIGHT_ACTION_TYPE = "incorrect_weight_charges";

// Mirrors backend/models/incorrect_weight_charges.py — the backend re-validates.
const MAX_SHIPMENTS = 20;
const MAX_GROUPS = 20;
const MAX_BOXES = 10000;
const MAX_DIMENSION_CM = 1000;
const LRN_RE = /^[A-Za-z0-9-]{1,40}$/;
const DIMENSION_RE = /^\d+(\.\d{1,2})?$/;

// Dimension-entry sections only: every id here MUST be a group array on the
// shipment object (see emptyShipment) — validateShipments and ShipmentEntry
// iterate it. The per-LRN "Total Boxes Sent" is its own entered field
// (shipment.totalBoxes) — never derived from these sections, which describe the
// SAME boxes from two sides.
const SECTIONS = [
  {
    id: "incorrect",
    title: "Incorrect Weight — Delhivery Record",
    emailHeading: "Delhivery Recorded Dimensions (Incorrect Weight):",
    tone: "border-red-200 bg-red-50/40",
  },
  {
    id: "correct",
    title: "Correct Weight — Client Confirmation",
    emailHeading: "Client Confirmed Dimensions (Correct Weight):",
    tone: "border-emerald-200 bg-emerald-50/40",
  },
];
const DIM_FIELDS = [
  { id: "boxes", label: "Boxes", aria: "Number of boxes" },
  { id: "length", label: "Length (cm)", aria: "Length in cm" },
  { id: "breadth", label: "Breadth (cm)", aria: "Breadth in cm" },
  { id: "height", label: "Height (cm)", aria: "Height in cm" },
];

let seq = 0;
const uid = () => `iwc-${++seq}`;
const emptyGroup = () => ({ key: uid(), boxes: "", length: "", breadth: "", height: "" });
const emptyShipment = () => ({
  key: uid(), lrn: "", orderId: "", totalBoxes: "", incorrect: [emptyGroup()], correct: [emptyGroup()],
});

// ── validation ─────────────────────────────────────────────────────────────────

function fieldError(field, raw) {
  const v = String(raw ?? "").trim();
  if (!v) return "Required";
  const n = Number(v);
  if (Number.isNaN(n)) return "Enter a number";
  if (n <= 0) return "Must be greater than 0";
  if (field === "boxes") {
    if (!Number.isInteger(n)) return "Whole number only";
    if (n > MAX_BOXES) return `Max ${MAX_BOXES.toLocaleString("en-IN")}`;
    return null;
  }
  if (!DIMENSION_RE.test(v)) return "Up to 2 decimals";
  if (n > MAX_DIMENSION_CM) return `Max ${MAX_DIMENSION_CM} cm`;
  return null;
}

function lrnError(raw, duplicates) {
  const v = (raw || "").trim();
  if (!v) return "LRN number is required";
  if (!LRN_RE.test(v)) return "Letters, digits and hyphens only (max 40)";
  if (duplicates.has(v.toUpperCase())) return "Duplicate LRN — each LRN can appear once";
  return null;
}

// Order ID is only used to find the order, so it is optional; when typed it must be well formed.
const orderIdError = (raw) => {
  const v = (raw || "").trim();
  return v && !LRN_RE.test(v) ? "Letters, digits and hyphens only (max 40)" : null;
};

/** { [shipmentKey]: { lrn, orderId, totalBoxes, groups: { [groupKey]: { field: msg } } } } + total count. */
function validateShipments(shipments) {
  const counts = {};
  shipments.forEach((s) => {
    const k = s.lrn.trim().toUpperCase();
    if (k) counts[k] = (counts[k] || 0) + 1;
  });
  const duplicates = new Set(Object.keys(counts).filter((k) => counts[k] > 1));

  const errors = {};
  let count = 0;
  shipments.forEach((s) => {
    const entry = {
      lrn: lrnError(s.lrn, duplicates), orderId: orderIdError(s.orderId),
      totalBoxes: fieldError("boxes", s.totalBoxes), groups: {},
    };
    if (entry.lrn) count += 1;
    if (entry.orderId) count += 1;
    if (entry.totalBoxes) count += 1;
    SECTIONS.forEach(({ id }) => {
      s[id].forEach((g) => {
        const ge = {};
        DIM_FIELDS.forEach(({ id: f }) => {
          const msg = fieldError(f, g[f]);
          if (msg) { ge[f] = msg; count += 1; }
        });
        if (Object.keys(ge).length) entry.groups[g.key] = ge;
      });
    });
    errors[s.key] = entry;
  });
  return { errors, count };
}

// ── formatting (mirrors fmt_num / fmt_group / build_subject in the service) ────

const fmtNum = (v) => Number(v).toFixed(2).replace(/\.?0+$/, "");
const sectionTotal = (groups) =>
  groups.reduce((sum, g) => (fieldError("boxes", g.boxes) ? sum : sum + Number(g.boxes)), 0);

function fmtGroup(g) {
  const ok = (f) => !fieldError(f, g[f]);
  const boxes = ok("boxes") ? Number(g.boxes) : null;
  const dim = (f) => (ok(f) ? fmtNum(g[f]) : "—");
  const unit = boxes === 1 ? "box" : "boxes";
  return `${boxes ?? "—"} ${unit}: ${dim("length")} × ${dim("breadth")} × ${dim("height")} cm`;
}

function buildSubject(lrns) {
  const list = lrns.length ? lrns.join(", ") : "—";
  return `Incorrect Weight Charges – ${lrns.length > 1 ? "LRN Nos." : "LRN No."} ${list}`;
}

// ── shipment entry ─────────────────────────────────────────────────────────────

function DimensionSection({ shipment, section, errors, showErr, onGroupChange, onAddGroup, onRemoveGroup }) {
  const groups = shipment[section.id];
  const total = sectionTotal(groups);
  // Non-blocking nudge only: Delhivery's count may legitimately differ — that can
  // be part of what is being disputed — but it usually flags a typo.
  const sent = fieldError("boxes", shipment.totalBoxes) ? null : Number(shipment.totalBoxes);
  const mismatch = sent !== null && total > 0 && total !== sent;
  return (
    <div className={`rounded-lg border p-3 ${section.tone}`}>
      <p className="text-sm font-medium text-gray-800">{section.title}</p>
      <div className="space-y-3 mt-2">
        {groups.map((g, gi) => (
          <div key={g.key} className="flex items-start gap-2" data-testid={`group-${section.id}-${gi}`}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-0">
              {DIM_FIELDS.map((f) => {
                const err = showErr(g[f.id]) ? errors?.[g.key]?.[f.id] : null;
                const inputId = `${g.key}-${f.id}`;
                return (
                  <div key={f.id} className="min-w-0">
                    <label htmlFor={inputId} className="block text-xs text-gray-600 truncate">{f.label}</label>
                    <Input id={inputId} type="number" inputMode={f.id === "boxes" ? "numeric" : "decimal"}
                      min={f.id === "boxes" ? 1 : 0.01} step={f.id === "boxes" ? 1 : 0.01}
                      aria-label={`${f.aria}, group ${gi + 1}`} aria-invalid={Boolean(err)}
                      className={`mt-0.5 bg-white ${err ? "border-red-500" : ""}`}
                      value={g[f.id]}
                      onChange={(e) => onGroupChange(g.key, f.id, e.target.value)} />
                    {err && <p className="text-xs text-red-600 mt-0.5">{err}</p>}
                  </div>
                );
              })}
            </div>
            <Button type="button" variant="ghost" size="icon"
              className="mt-5 shrink-0 text-red-500 hover:text-red-700"
              disabled={groups.length === 1}
              title={groups.length === 1 ? "At least one dimension group is required" : "Delete dimension group"}
              aria-label={`Delete dimension group ${gi + 1}`}
              onClick={() => onRemoveGroup(g.key)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <Button type="button" variant="outline" size="sm" className="bg-white"
          disabled={groups.length >= MAX_GROUPS} onClick={onAddGroup}
          data-testid={`add-group-${section.id}`}>
          <Plus className="w-3.5 h-3.5 mr-1" />Add Dimension Group
        </Button>
        <span className="text-sm text-gray-700">
          Total boxes: <span className="font-medium tabular-nums">{total.toLocaleString("en-IN")}</span>
        </span>
      </div>
      {mismatch && (
        <p className="text-xs text-amber-700 mt-2" data-testid={`mismatch-${section.id}`}>
          Differs from Total Boxes Sent ({sent.toLocaleString("en-IN")}) — check the box counts.
        </p>
      )}
    </div>
  );
}

function ShipmentEntry({ index, shipment, errors, showAll, canRemove, onChange }) {
  // Before the first send attempt only fields the user has typed into show errors.
  const showErr = (value) => showAll || String(value ?? "").trim() !== "";
  const lrnErr = showErr(shipment.lrn) ? errors?.lrn : null;
  const lrnId = `${shipment.key}-lrn`;
  const orderErr = errors?.orderId || null;
  const orderIdInput = `${shipment.key}-order-id`;
  const totalErr = showErr(shipment.totalBoxes) ? errors?.totalBoxes : null;
  const totalId = `${shipment.key}-total-boxes`;

  const [fetching, setFetching] = useState(false);
  const [note, setNote] = useState(null); // { tone, text } — result of the last Fetch details
  const [pending, setPending] = useState(null); // a fetched order waiting for "replace what I typed?"
  // The latest shipment, so a fetch that finishes after the user kept typing never overwrites that typing.
  const latest = useRef(shipment);
  latest.current = shipment;

  const setGroups = (section, fn) =>
    onChange({ ...shipment, [section]: fn(shipment[section]) });

  const lrnTyped = shipment.lrn.trim();
  const orderTyped = shipment.orderId.trim();
  const canFetch = Boolean(lrnTyped || orderTyped)
    && (!lrnTyped || LRN_RE.test(lrnTyped)) && (!orderTyped || LRN_RE.test(orderTyped));

  const apply = (result) => {
    onChange({ ...latest.current, ...prefillFromLookup(result, emptyGroup) });
    setNote(lookupMessage(result));
  };

  const fetchDetails = async () => {
    if (!canFetch || fetching) return;
    setFetching(true);
    setNote(null);
    try {
      const { data } = await axios.get(`${API}/notification-config/admin/incorrect-weight-charges/lookup`, {
        params: { ...(lrnTyped ? { lrn: lrnTyped } : {}), ...(orderTyped ? { order_id: orderTyped } : {}) },
      });
      if (data.status !== "found") setNote(lookupMessage(data));
      else if (fetchWouldOverwrite(latest.current)) setPending(data);
      else apply(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not fetch the order"));
    } finally {
      setFetching(false);
    }
  };
  const fetchOnEnter = (e) => { if (e.key === "Enter") { e.preventDefault(); fetchDetails(); } };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3" data-testid={`shipment-${index}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <label htmlFor={lrnId} className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                LRN Number #{index + 1}
              </label>
              <Input id={lrnId} className={`mt-1 ${lrnErr ? "border-red-500" : ""}`} placeholder="e.g. 314341943"
                aria-invalid={Boolean(lrnErr)} value={shipment.lrn} maxLength={40}
                onChange={(e) => onChange({ ...shipment, lrn: e.target.value })} onKeyDown={fetchOnEnter}
                data-testid={`lrn-input-${index}`} />
              {lrnErr && <p className="text-xs text-red-600 mt-1">{lrnErr}</p>}
            </div>
            <div className="min-w-0">
              <label htmlFor={orderIdInput} className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order ID
              </label>
              <Input id={orderIdInput} className={`mt-1 ${orderErr ? "border-red-500" : ""}`} placeholder="e.g. EDGO-041"
                aria-invalid={Boolean(orderErr)} value={shipment.orderId} maxLength={40}
                onChange={(e) => onChange({ ...shipment, orderId: e.target.value })} onKeyDown={fetchOnEnter}
                data-testid={`order-id-input-${index}`} />
              {orderErr && <p className="text-xs text-red-600 mt-1">{orderErr}</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Button type="button" variant="outline" size="sm" disabled={!canFetch || fetching}
              onClick={fetchDetails} data-testid={`fetch-details-${index}`}>
              {fetching ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
              Fetch details
            </Button>
            <p className={`text-xs min-w-0 ${note?.tone === "warn" ? "text-amber-700" : note ? "text-emerald-700" : "text-gray-500"}`}
              role={note ? "status" : undefined} data-testid={`fetch-note-${index}`}>
              {note?.text
                || "Enter the LRN or the Order ID (use the Order ID if the LRN is on more than one order), then fetch to prefill the Delhivery record."}
            </p>
          </div>
          <div className="w-full sm:w-44">
            <label htmlFor={totalId} className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total Boxes Sent
            </label>
            <Input id={totalId} type="number" inputMode="numeric" min={1} step={1}
              className={`mt-1 ${totalErr ? "border-red-500" : ""}`} placeholder="e.g. 50"
              aria-invalid={Boolean(totalErr)} value={shipment.totalBoxes}
              onChange={(e) => onChange({ ...shipment, totalBoxes: e.target.value })}
              data-testid={`total-boxes-input-${index}`} />
            {totalErr && <p className="text-xs text-red-600 mt-1">{totalErr}</p>}
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" className="mt-6 shrink-0 text-red-500 hover:text-red-700"
          disabled={!canRemove} aria-label={`Delete LRN #${index + 1}`}
          title={canRemove ? "Delete this LRN" : "At least one LRN is required"}
          onClick={() => onChange(null)} data-testid={`remove-lrn-${index}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      {SECTIONS.map((section) => (
        <DimensionSection key={section.id} shipment={shipment} section={section}
          errors={errors?.groups} showErr={showErr}
          onGroupChange={(gk, f, v) => setGroups(section.id, (gs) => gs.map((g) => (g.key === gk ? { ...g, [f]: v } : g)))}
          onAddGroup={() => setGroups(section.id, (gs) => [...gs, emptyGroup()])}
          onRemoveGroup={(gk) => setGroups(section.id, (gs) => gs.filter((g) => g.key !== gk))} />
      ))}
      <ConfirmDialog
        open={Boolean(pending)}
        onOpenChange={(o) => { if (!o) setPending(null); }}
        title="Replace the values you entered?"
        description={`Fetching order ${pending?.order_id || ""} will replace Total Boxes Sent and the Delhivery Record dimensions on this LRN. Client Confirmation is not changed.`}
        confirmLabel="Replace with fetched values"
        onConfirm={() => { apply(pending); setPending(null); }}
      />
    </div>
  );
}

// ── preview ────────────────────────────────────────────────────────────────────

function EmailPreview({ sender, to, cc, bcc, shipments }) {
  const lrns = shipments.map((s) => s.lrn.trim()).filter(Boolean);
  const many = shipments.length > 1;

  return (
    <div className="text-sm">
      <EmailPreviewHeader sender={sender} to={to} cc={cc} bcc={bcc}
        subject={buildSubject(lrns)} subjectTestId="iwc-preview-subject" />
      <div className="pt-3 space-y-3 text-gray-800" data-testid="iwc-preview-body">
        <p>Hi Delhivery Team,</p>
        <p>
          We have received an escalation regarding discrepancies in the dimensions and weight recorded
          for the following {many ? "shipments" : "shipment"}.
        </p>
        {shipments.map((s) => {
          // Entered per LRN — not derived from the incorrect/correct sections.
          const totalBoxes = fieldError("boxes", s.totalBoxes) ? "—" : Number(s.totalBoxes);

          return (
            <div
              key={s.key}
              className="border-l-4 border-teal-600 bg-slate-50 px-4 py-3"
            >
              <p className="font-medium break-all">
                LRN No. {s.lrn.trim() || "—"}
              </p>
              
              <p className="font-medium" data-testid="iwc-preview-total-boxes">
                Total Boxes Sent: {totalBoxes}
              </p>


              <div className="mt-2">
                <p className="font-medium">
                  Delhivery Recorded Dimensions (Incorrect Weight):
                </p>
                <ul className="list-disc pl-5">
                  {s.incorrect.map((g) => (
                    <li key={g.key}>{fmtGroup(g)}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-2">
                <p className="font-medium">
                  Client Confirmed Dimensions (Correct Weight):
                </p>
                <ul className="list-disc pl-5">
                  {s.correct.map((g) => (
                    <li key={g.key}>{fmtGroup(g)}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
        <p>
          Kindly verify the shipment dimensions and weight, investigate the {many ? "discrepancies" : "discrepancy"},
          and review the applicable charges for correction.
        </p>
        <p>Please let us know if any additional information is required.</p>
        <p>Regards,<br />{sender?.from_name || "EdgeOS Support Team "}</p>
      </div>
    </div>
  );
}

// ── panel ──────────────────────────────────────────────────────────────────────

export default function IncorrectWeightChargesPanel({ user, configs, loading, onConfigSaved }) {
  const recipients = useTemplateRecipients({
    actionType: INCORRECT_WEIGHT_ACTION_TYPE, senderPath: "incorrect-weight-charges/sender",
    user, configs, onConfigSaved,
  });
  const { to, cc, bcc, sender, sendBlocker } = recipients;

  const [shipments, setShipments] = useState(() => [emptyShipment()]);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const { errors, count: errorCount } = useMemo(() => validateShipments(shipments), [shipments]);

  const updateShipment = (key, next) =>
    setShipments((prev) => (next === null ? prev.filter((s) => s.key !== key) : prev.map((s) => (s.key === key ? next : s))));

  const requestSend = () => {
    setShowAllErrors(true);
    if (sendBlocker) { toast.error(sendBlocker); return; }
    if (errorCount) { toast.error(`Fix ${errorCount} highlighted field${errorCount === 1 ? "" : "s"} before sending`); return; }
    setConfirmOpen(true);
  };

  const send = async () => {
    if (sending) return;
    setSending(true);
    const payload = {
      shipments: shipments.map((s) => {
        const groups = (gs) => gs.map((g) => ({
          boxes: Number(g.boxes), length: Number(g.length), breadth: Number(g.breadth), height: Number(g.height),
        }));
        return {
          lrn: s.lrn.trim(), total_boxes: Number(s.totalBoxes),
          incorrect: groups(s.incorrect), correct: groups(s.correct),
        };
      }),
    };
    try {
      const res = await axios.post(`${API}/notification-config/admin/incorrect-weight-charges/send`, payload);
      if (res.data?.success) {
        toast.success(res.data.message || "Email sent");
        // Cleared so a second click cannot resend the same escalation by accident.
        setShipments([emptyShipment()]);
        setShowAllErrors(false);
      } else {
        toast.error(res.data?.error || "The email could not be sent");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "The email could not be sent", { shipments: "LRN" }));
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  if (loading && !configs.length) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" aria-busy="true">
        {[0, 1].map((i) => <div key={i} className="h-72 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  const lrnList = shipments.map((s) => s.lrn.trim()).filter(Boolean);

  return (
    <div className="space-y-6" data-testid="incorrect-weight-charges-panel">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Scale className="w-5 h-5 text-teal-600 shrink-0" />
          Incorrect Weight Charges Email Notification Template
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Report shipments whose dimensions or weight were recorded incorrectly. The email preview updates as you type.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="space-y-6 min-w-0">
          {/* Recipients — saved configuration, separate from the shipment data */}
          <RecipientsCard recipients={recipients} idPrefix="iwc"
            description="Saved configuration. Every email from this form goes to the saved lists." />

          {/* Shipments — per-email data, never saved */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-gray-500" />Shipments
                  <Badge variant="secondary" className="text-xs">{shipments.length}</Badge>
                </CardTitle>
                <Button type="button" variant="outline" disabled={shipments.length >= MAX_SHIPMENTS}
                  onClick={() => setShipments((prev) => [...prev, emptyShipment()])} data-testid="iwc-add-lrn">
                  <Plus className="w-4 h-4 mr-1" />Add LRN
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Each LRN keeps its own dimension groups. Up to {MAX_SHIPMENTS} LRNs per email.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {shipments.map((s, i) => (
                <ShipmentEntry key={s.key} index={i} shipment={s} errors={errors[s.key]}
                  showAll={showAllErrors} canRemove={shipments.length > 1}
                  onChange={(next) => updateShipment(s.key, next)} />
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Preview — sticky beside the form on wide screens, below it otherwise */}
        <Card className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-lg">Email Preview</CardTitle>
              <Button type="button" className="bg-teal-600 hover:bg-teal-700"
                onClick={requestSend} disabled={sending} data-testid="iwc-trigger-now">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Trigger Now
              </Button>
            </div>
            {sendBlocker && <p className="text-xs text-amber-700">{sendBlocker}</p>}
            {!sendBlocker && showAllErrors && errorCount > 0 && (
              <p className="text-xs text-red-600">{errorCount} field{errorCount === 1 ? "" : "s"} need attention before sending.</p>
            )}
          </CardHeader>
          <CardContent>
            <EmailPreview sender={sender} to={to} cc={cc} bcc={bcc} shipments={shipments} />
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!sending) setConfirmOpen(o); }}
        title="Send Incorrect Weight Charges email?"
        description={`"${buildSubject(lrnList)}" will be emailed to ${to.length} To, ${cc.length} CC and ${bcc.length} BCC recipient(s). This sends a real email and cannot be undone.`}
        confirmLabel="Send Email"
        loading={sending}
        onConfirm={send}
      />
    </div>
  );
}
